import React, { lazy, Suspense } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import SuperAdminLayout from "@/components/layout/SuperAdminLayout";
import SuperAdminAuthWrapper from "@/components/layout/SuperAdminAuthWrapper";


// Lazy load components for better performance
const SuperAdminDashboard = lazy(() => import("@/pages/superadmin/SuperAdminDashboard"));
const OrganizationsManagement = lazy(() => import("@/pages/superadmin/OrganizationsManagement"));
const UserManagement = lazy(() => import("@/pages/superadmin/UserManagement"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SubscriptionManagement = lazy(() => import("@/pages/superadmin/SubscriptionManagement"));
const SystemSettings = lazy(() => import("@/pages/superadmin/SystemSettings"));
const SuperadminOperationalTracker = lazy(() => import("@/pages/superadmin/SuperadminOperationalTracker.jsx"));

// Stripe management components
const StripePlansViewer = lazy(() => import("@/pages/superadmin/StripePlansViewer"));
const StripeCustomersViewer = lazy(() => import("@/pages/superadmin/StripeCustomersViewer"));
const StripeSubscriptionsManager = lazy(() => import("@/pages/superadmin/StripeSubscriptionsManager"));
const StripeCTADebugger = lazy(() => import("@/pages/superadmin/StripeCTADebugger"));
const SubscriptionAutomation = lazy(() => import("@/pages/superadmin/SubscriptionAutomation"));
const AutomationTemplates = lazy(() => import("@/pages/superadmin/AutomationTemplates"));
const PricingValidator = lazy(() => import("@/pages/superadmin/PricingValidator"));
const SemanticAnalysis = lazy(() => import("@/pages/superadmin/SemanticAnalysis"));
const StripeConnectRequests = lazy(() => import("@/pages/superadmin/StripeConnectRequests"));
const BizAdminDesignSystem = lazy(() => import("@/pages/superadmin/BizAdminDesignSystem"));
const AdminChatPanel = lazy(() => import("@/pages/superadmin/AdminChatPanel"));

// Loading spinner for lazy-loaded components
const LazyLoadingSpinner = () => (
  <div className="flex justify-center items-center h-full py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Wrapper component to use with the element prop
const SuperAdminPortalAuthWrapper = () => {
  return (
    <SuperAdminAuthWrapper>
      <SuperAdminLayout>
        <Outlet />
      </SuperAdminLayout>
    </SuperAdminAuthWrapper>
  );
};

const SuperAdminRoutes = () => {
  return (
    <Routes>
      <Route element={<SuperAdminPortalAuthWrapper />}>
        {/* Dashboard route */}
        <Route 
          index 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SuperAdminDashboard />
            </Suspense>
          } 
        />
        
        {/* Platform Management */}
        <Route 
          path="organizations" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <OrganizationsManagement />
            </Suspense>
          } 
        />
        
        <Route 
          path="users" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <UserManagement />
            </Suspense>
          } 
        />
        
        <Route 
          path="subscription-management" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SubscriptionManagement />
            </Suspense>
          } 
        />
        
        <Route 
          path="semantic-analysis" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SemanticAnalysis />
            </Suspense>
          } 
        />
        
        {/* Operational Tracker */}
        <Route 
          path="operational-tracker" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SuperadminOperationalTracker />
            </Suspense>
          } 
        />
        
        {/* Stripe Management */}
        <Route 
          path="stripe-plans" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <StripePlansViewer />
            </Suspense>
          } 
        />
        
        <Route 
          path="customers" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <StripeCustomersViewer />
            </Suspense>
          } 
        />
        
        <Route 
          path="subscriptions" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <StripeSubscriptionsManager />
            </Suspense>
          } 
        />
        
        <Route 
          path="cta-debug" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <StripeCTADebugger />
            </Suspense>
          } 
        />
        
        <Route 
          path="automation" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SubscriptionAutomation />
            </Suspense>
          } 
        />
        
        <Route 
          path="templates" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <AutomationTemplates />
            </Suspense>
          } 
        />
        
        <Route 
          path="pricing-validator" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <PricingValidator />
            </Suspense>
          } 
        />
        
        <Route 
          path="stripe-connect-requests" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <StripeConnectRequests />
            </Suspense>
          } 
        />
        
        {/* System Health */}
        <Route 
          path="system" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <SystemSettings />
            </Suspense>
          } 
        />
        
        {/* BizAdmin Design System */}
        <Route 
          path="bizadmin-design-system" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <BizAdminDesignSystem />
            </Suspense>
          } 
        />
        
        {/* Admin Chat Panel */}
        <Route 
          path="admin-chat" 
          element={
            <Suspense fallback={<LazyLoadingSpinner />}>
              <AdminChatPanel />
            </Suspense>
          } 
        />
        
      </Route>
      
      {/* Fallback route */}
      <Route 
        path="*" 
        element={
          <Suspense fallback={<LazyLoadingSpinner />}>
            <NotFound />
          </Suspense>
        } 
      />
    </Routes>
  );
};

export default SuperAdminRoutes; 